import { useState, useCallback } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAppUser } from "./useAppUser";
import { Recipe } from "../types/mealPlan";

export type QuickBiteRequest = {
  pantryItemIds?: string[];
  customIngredients?: string[];
  servings: number;
  customInstructions?: string;
  images?: File[];
  audio?: Blob;
  videoFileId?: string;
};

type JobStatus = {
  status: "pending" | "processing" | "complete" | "failed";
  recipes?: Recipe[];
  error?: string;
};

const fetchJobStatus = async (
  jobId: string,
  token: string
): Promise<JobStatus> => {
  const statusResponse = await fetch(
    `${process.env.REACT_APP_API_URL}/api/recipes/quick-bites/status/${jobId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!statusResponse.ok) {
    throw new Error("Failed to fetch job status");
  }

  return statusResponse.json();
};

// How long to wait for a job before giving up. Generation runs on the meal plan
// Cloud Tasks queue, which retries up to 3 times, and a single attempt takes
// roughly a minute, so a 2-minute cap gave up on jobs that were still working
// and would go on to succeed.
const JOB_WAIT_TIMEOUT_MS = 5 * 60 * 1000;

// Watch the job doc until it reaches a terminal status. Resolves with the
// terminal outcome, "timeout" after the cap above, or "listener-error"
// when the realtime listener can't be established.
const waitForJobCompletion = (
  uid: string,
  jobId: string
): Promise<"complete" | "failed" | "timeout" | "listener-error"> =>
  new Promise((resolve) => {
    const jobRef = doc(db, "users", uid, "quick_bite_jobs", jobId);
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const settle = (
      outcome: "complete" | "failed" | "timeout" | "listener-error"
    ) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      resolve(outcome);
    };

    timeoutId = setTimeout(() => settle("timeout"), JOB_WAIT_TIMEOUT_MS);

    unsubscribe = onSnapshot(
      jobRef,
      (snapshot) => {
        const status = snapshot.data()?.status;
        if (status === "complete") {
          settle("complete");
        } else if (status === "failed") {
          settle("failed");
        }
        // Keep waiting while status is "pending" or "processing"
      },
      (listenerError) => {
        console.error("Quick bite job listener failed:", listenerError);
        settle("listener-error");
      }
    );
    if (settled) unsubscribe();
  });

const pollForJobCompletion = async (
  jobId: string,
  token: string
): Promise<Recipe[]> => {
  const pollInterval = 2000; // 2 seconds
  const maxAttempts = JOB_WAIT_TIMEOUT_MS / pollInterval;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, pollInterval));

    const jobStatus = await fetchJobStatus(jobId, token);

    if (jobStatus.status === "complete" && jobStatus.recipes) {
      return jobStatus.recipes;
    }

    if (jobStatus.status === "failed") {
      throw new Error(jobStatus.error || "Recipe generation failed");
    }

    // Continue polling if status is "pending" or "processing"
  }

  throw new Error("Recipe generation timed out");
};

const waitForJobRecipes = async (
  uid: string,
  jobId: string,
  token: string
): Promise<Recipe[]> => {
  const outcome = await waitForJobCompletion(uid, jobId);

  if (outcome === "timeout") {
    // The listener can miss the terminal write (dropped stream, backgrounded
    // tab), which reported a timeout for a job the server had finished. Ask the
    // server before believing the clock.
    const jobStatus = await fetchJobStatus(jobId, token);
    if (jobStatus.status === "complete" && jobStatus.recipes) {
      return jobStatus.recipes;
    }
    if (jobStatus.status === "failed") {
      throw new Error(jobStatus.error || "Recipe generation failed");
    }
    throw new Error("Recipe generation timed out");
  }

  if (outcome === "listener-error") {
    // Fall back to the legacy REST polling loop
    return pollForJobCompletion(jobId, token);
  }

  // Terminal status reached: the job doc only stores recipe IDs, so read
  // the composed result (recipes or error message) over REST.
  const jobStatus = await fetchJobStatus(jobId, token);
  if (jobStatus.status === "complete" && jobStatus.recipes) {
    return jobStatus.recipes;
  }
  throw new Error(jobStatus.error || "Recipe generation failed");
};

export const useQuickBites = () => {
  // useAppUser rather than useAuth: under admin impersonation the merged
  // user's uid is the impersonated user's, which is where the backend
  // creates the quick bite job doc the realtime listener watches.
  const { user } = useAppUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateQuickBites = useCallback(
    async (request: QuickBiteRequest): Promise<Recipe[]> => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();

        // Use FormData if images are provided
        const formData = new FormData();

        if (request.pantryItemIds) {
          formData.append('pantryItemIds', JSON.stringify(request.pantryItemIds));
        }

        if (request.customIngredients) {
          formData.append('customIngredients', JSON.stringify(request.customIngredients));
        }

        formData.append('servings', request.servings.toString());

        if (request.customInstructions) {
          formData.append('customInstructions', request.customInstructions);
        }

        if (request.images) {
          request.images.forEach((image) => {
            formData.append('images', image);
          });
        }

        if (request.audio) {
          formData.append('audio', request.audio, 'recording.webm');
        }

        if (request.videoFileId) {
          formData.append('videoFileId', request.videoFileId);
        }

        // Step 1: Create job
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/recipes/quick-bite`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to generate quick bites");
        }

        const { jobId } = await response.json();

        // Step 2: Wait for completion via a realtime listener on the job doc
        // (falls back to REST polling if the listener can't be established)
        const recipes = await waitForJobRecipes(user.uid, jobId, token);

        return recipes;
      } catch (error: any) {
        console.error("Error generating quick bites:", error);
        setError(error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const saveQuickBites = useCallback(
    async (recipeIds: string[]): Promise<Recipe[]> => {
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/quick-bites/save`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ recipeIds }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save quick bites");
      }

      const data = await response.json();
      return data.recipes;
    },
    [user]
  );

  const fetchQuickBites = useCallback(async (): Promise<Recipe[]> => {
    if (!user) {
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes/quick-bites`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch quick bites");
      }

      const data = await response.json();
      return data.recipes || [];
    } catch (error: any) {
      console.error("Error fetching quick bites:", error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    generateQuickBites,
    saveQuickBites,
    fetchQuickBites,
    loading,
    error,
  };
};
